"use client";

import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@/components/atoms/card";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";

import { useRef } from "react";
import { formatDateToFriendlyFormat } from "@/lib/utils";

interface LineChartProps {
  data:
    | { name: string; value: number }[]
    | {
        date: string;
        totalOrders: number;
        completedOrders: number;
        avgCompletionTime: number;
      }[];
  title: string;
  isDualLine?: boolean;
}

// Helper to duplicate the single data point for recharts line rendering
function ensureAtLeastTwoPoints<T extends object>(
  arr: T[],
  duplicateKeys: string[],
): T[] {
  if (arr.length === 1) {
    // Duplicate the point, but offset the x value slightly for recharts
    const first = arr[0];
    // Try to find a key for x axis (date or name)
    const newPoint: any = { ...first };
    for (const key of duplicateKeys) {
      if (key in first) {
        // If it's a date, try to add a day, else append a marker
        if (key === "date" || key === "name") {
          const val = first[key as keyof typeof first];
          if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}/.test(val)) {
            // Add one day
            const d = new Date(val);
            d.setDate(d.getDate() + 1);
            newPoint[key] = d.toISOString().slice(0, 10);
          } else if (typeof val === "string") {
            newPoint[key] = val + " (2)";
          } else if (typeof val === "number") {
            newPoint[key] = val + 1;
          }
        }
      }
    }
    return [first, newPoint];
  }
  return arr;
}

export function OrderLineChart({
  data,
  title,
  isDualLine = false,
}: LineChartProps) {
  const chartElementRef = useRef<HTMLDivElement>(null);

  // Transform data for dual line chart
  const transformedData = isDualLine
    ? (
        data as {
          date: string;
          totalOrders: number;
          completedOrders: number;
          avgCompletionTime: number;
        }[]
      ).map((item) => ({
        name: item.date,
        date: item.date,
        totalOrders: item.totalOrders,
        completedOrders: item.completedOrders,
        avgCompletionTime: item.avgCompletionTime,
      }))
    : (data as { name: string; value: number }[]);

  // Ensure at least two points for recharts to render a line
  const chartData = ensureAtLeastTwoPoints(
    transformedData as any,
    isDualLine ? ["date", "name"] : ["name"],
  );

  // Check if we need dual Y-axis by comparing scales
  const needsDualAxis =
    isDualLine &&
    (() => {
      const totalOrdersValues = (transformedData as any[]).map(
        (d) => d.totalOrders,
      );
      const completedOrdersValues = (transformedData as any[]).map(
        (d) => d.completedOrders,
      );

      const maxTotal = Math.max(...totalOrdersValues);
      const maxCompleted = Math.max(...completedOrdersValues);

      // Use dual axis if the scales differ significantly
      return (
        maxCompleted > 0 &&
        (maxTotal / maxCompleted > 3 || maxCompleted / maxTotal > 3)
      );
    })();

  // Check if there's data to display
  const hasData = transformedData && transformedData.length > 0;
  return (
    <Card
      className="h-full pb-10 bg-white w-full min-w-0 "
      ref={chartElementRef}
      id="line-chart-card"
    >
      <CardHeader className="pb-2 mb-4 sm:mb-6 flex flex-row items-center justify-between">
        <CardTitle className="text-base font-medium truncate flex-1 mr-2">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4">
        {hasData ? (
          <>
            <div className="h-48 sm:h-56 lg:h-64 w-full min-w-0">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={chartData}
                  margin={{
                    top: 5,
                    right: needsDualAxis ? 35 : 5,
                    left: 5,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="date"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10 }}
                    tickFormatter={(dateStr) =>
                      formatDateToFriendlyFormat(dateStr)
                    }
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    yAxisId="left"
                    dataKey="totalOrders"
                    name="Total órdenes"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10 }}
                    width={30}
                  />
                  {needsDualAxis && (
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      dataKey="completedOrders"
                      name="Órdenes completadas"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 10 }}
                      width={30}
                      label={{
                        value: "Órdenes completadas",
                        angle: 90,
                        position: "insideRight",
                      }}
                    />
                  )}

                  <Tooltip
                    isAnimationActive={false}
                    formatter={(value: number, name: string) => {
                      if (name === "totalOrders" || name === "Total órdenes") {
                        return [`${value}`, "Total órdenes"];
                      }
                      if (
                        name === "completedOrders" ||
                        name === "Órdenes completadas"
                      ) {
                        return [`${value}`, "Órdenes completadas"];
                      }

                      return [value, name];
                    }}
                    labelFormatter={(dateStr) =>
                      formatDateToFriendlyFormat(dateStr)
                    }
                  />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="totalOrders"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={{
                      r: 3,
                      fill: "#3b82f6",
                      stroke: "#fff",
                      strokeWidth: 1,
                    }}
                    activeDot={{ r: 5, fill: "#3b82f6" }}
                    name="Total órdenes"
                  />
                  <Line
                    yAxisId={needsDualAxis ? "right" : "left"}
                    type="monotone"
                    dataKey="completedOrders"
                    stroke="#10b981"
                    strokeWidth={2}
                    dot={{
                      r: 3,
                      fill: "#10b981",
                      stroke: "#fff",
                      strokeWidth: 1,
                    }}
                    activeDot={{ r: 5, fill: "#10b981" }}
                    name="Órdenes completadas"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center mt-4 gap-6 flex-wrap">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className="text-xs text-gray-600">
                  Total órdenes diarias
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-xs text-gray-600">
                  Órdenes completadas diarias
                </span>
              </div>
            </div>
          </>
        ) : (
          <div className="h-48 sm:h-56 lg:h-64 w-full flex flex-col items-center justify-center text-gray-500">
            <svg
              className="w-12 h-12 mb-4 text-gray-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
            <p className="text-sm font-medium mb-1">No hay datos disponibles</p>
            <p className="text-xs text-center max-w-xs">
              {isDualLine
                ? "No se encontraron datos de viajes o conductores para el período seleccionado"
                : "No se encontraron datos para el período seleccionado"}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface DriversLineChartProps {
  data:
    | { name: string; value: number }[]
    | {
        date: string;
        average_trips_per_driver: number;
        total_active_drivers: number;
        total_trips: number;
      }[];
  title: string;
  isDualLine?: boolean;
}

export function DriversLineChart({
  data,
  title,
  isDualLine = false,
}: DriversLineChartProps) {
  const chartElementRef = useRef<HTMLDivElement>(null);

  // Transform data for dual line chart
  const transformedData = isDualLine
    ? (
        data as {
          date: string;
          average_trips_per_driver: number;
          total_active_drivers: number;
          total_trips: number;
        }[]
      ).map((item) => ({
        name: item.date,
        date: item.date,
        averageTrips: item.average_trips_per_driver,
        activeDrivers: item.total_active_drivers,
      }))
    : (data as { name: string; value: number }[]);

  // Ensure at least two points for recharts to render a line
  const chartData = ensureAtLeastTwoPoints(
    transformedData as any,
    isDualLine ? ["date", "name"] : ["name"],
  );

  // Check if we need dual Y-axis by comparing scales
  const needsDualAxis =
    isDualLine &&
    (() => {
      const avgTripsValues = (transformedData as any[]).map(
        (d) => d.averageTrips,
      );
      const activeDriversValues = (transformedData as any[]).map(
        (d) => d.activeDrivers,
      );

      const maxAvgTrips = Math.max(...avgTripsValues);
      const maxActiveDrivers = Math.max(...activeDriversValues);

      // Use dual axis if the scales differ significantly (more than 3x difference)
      return (
        maxActiveDrivers > 0 &&
        (maxAvgTrips / maxActiveDrivers > 3 ||
          maxActiveDrivers / maxAvgTrips > 3)
      );
    })();

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded shadow-lg">
          <p className="text-sm font-medium mb-2">
            {formatDateToFriendlyFormat(label)}
          </p>
          {isDualLine ? (
            <>
              {payload.find((p: any) => p.dataKey === "averageTrips") && (
                <p className="text-sm text-blue-600 flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                  Promedio viajes:{" "}
                  {payload
                    .find((p: any) => p.dataKey === "averageTrips")
                    ?.value.toFixed(1)}
                </p>
              )}
              {payload.find((p: any) => p.dataKey === "activeDrivers") && (
                <p className="text-sm text-cyan-600 flex items-center gap-2">
                  <span className="w-2 h-2 bg-cyan-500 rounded-full"></span>
                  Conductores activos:{" "}
                  {
                    payload.find((p: any) => p.dataKey === "activeDrivers")
                      ?.value
                  }
                </p>
              )}
            </>
          ) : (
            <p className="text-sm text-gray-600">Órdenes: {payload[0].value}</p>
          )}
        </div>
      );
    }
    return null;
  };

  // Check if there's data to display
  const hasData = transformedData && transformedData.length > 0;

  return (
    <Card
      className="h-full pb-10 bg-white w-full min-w-0 "
      ref={chartElementRef}
      id="line-chart-card"
    >
      <CardHeader className="pb-2 mb-4 sm:mb-6 flex flex-row items-center justify-between">
        <CardTitle className="text-base font-medium truncate flex-1 mr-2">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4">
        {hasData ? (
          <>
            <div className="h-48 sm:h-56 lg:h-64 w-full min-w-0">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={chartData}
                  margin={{
                    top: 5,
                    right: needsDualAxis ? 35 : 5,
                    left: 5,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10 }}
                    tickFormatter={(dateStr) =>
                      formatDateToFriendlyFormat(dateStr)
                    }
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    yAxisId="left"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10 }}
                    width={30}
                  />
                  {needsDualAxis && (
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 10 }}
                      width={30}
                      label={{
                        value: "Conductores",
                        angle: 90,
                        position: "insideRight",
                      }}
                    />
                  )}
                  <Tooltip content={<CustomTooltip />} />
                  {isDualLine ? (
                    <>
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="averageTrips"
                        stroke="#3b82f6"
                        strokeWidth={2}
                        dot={{ r: 3, fill: "#3b82f6" }}
                        activeDot={{ r: 5, fill: "#3b82f6" }}
                        name="Promedio viajes"
                      />
                      <Line
                        yAxisId={needsDualAxis ? "right" : "left"}
                        type="monotone"
                        dataKey="activeDrivers"
                        stroke="#06b6d4"
                        strokeWidth={2}
                        dot={{ r: 3, fill: "#06b6d4" }}
                        activeDot={{ r: 5, fill: "#06b6d4" }}
                        name="Conductores activos"
                      />
                    </>
                  ) : (
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="value"
                      stroke="#8884d8"
                      strokeWidth={2}
                      dot={{ r: 2 }}
                      activeDot={{ r: 4 }}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
            {isDualLine && (
              <div className="flex justify-center mt-4 gap-6">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  <span className="text-xs text-gray-600">
                    Promedio de viajes diarios
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-cyan-500 rounded-full"></div>
                  <span className="text-xs text-gray-600">
                    Total conductores activos diarios
                  </span>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="h-48 sm:h-56 lg:h-64 w-full flex flex-col items-center justify-center text-gray-500">
            <svg
              className="w-12 h-12 mb-4 text-gray-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
            <p className="text-sm font-medium mb-1">No hay datos disponibles</p>
            <p className="text-xs text-center max-w-xs">
              {isDualLine
                ? "No se encontraron datos de viajes o conductores para el período seleccionado"
                : "No se encontraron datos para el período seleccionado"}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
